import { AdminGrade, EntitlementEntry } from '../api';

/**
 * Phase 2 §5 — the grade/subject entitlement tree.
 *
 * Each grade is in exactly one of three states, and the UI says which:
 *   none    — no grant; the grade is invisible to the school
 *   whole   — one row (grade, null); every subject, including ones added later
 *   subjects— one row per ticked subject
 *
 * "Whole grade" is deliberately not the same as ticking every subject: only
 * the whole-grade grant follows the catalog as it grows. That distinction is
 * the reason this is a tri-state control rather than a checkbox tree, and the
 * hint text under each grade spells it out rather than leaving it to be
 * discovered later when a new subject fails to appear.
 */
export type GradeMode = 'none' | 'whole' | 'subjects';

export interface PickerValue {
  /** gradeId -> mode. Absent means 'none'. */
  modes: Record<string, GradeMode>;
  /** gradeId -> ticked subjectIds. Only meaningful when mode is 'subjects'. */
  subjects: Record<string, string[]>;
}

export const emptyPicker: PickerValue = { modes: {}, subjects: {} };

/** Server rows -> picker state. */
export function pickerFromEntitlements(rows: EntitlementEntry[]): PickerValue {
  const value: PickerValue = { modes: {}, subjects: {} };
  for (const r of rows) {
    if (!r.subjectId) {
      value.modes[r.gradeId] = 'whole';
      continue;
    }
    // A whole-grade row wins; the server normalizes the same way.
    if (value.modes[r.gradeId] !== 'whole') value.modes[r.gradeId] = 'subjects';
    value.subjects[r.gradeId] = [...(value.subjects[r.gradeId] ?? []), r.subjectId];
  }
  return value;
}

/** Picker state -> the payload the API expects. */
export function entitlementsFromPicker(value: PickerValue): EntitlementEntry[] {
  const out: EntitlementEntry[] = [];
  for (const [gradeId, mode] of Object.entries(value.modes)) {
    if (mode === 'whole') {
      out.push({ gradeId });
    } else if (mode === 'subjects') {
      for (const subjectId of value.subjects[gradeId] ?? []) {
        out.push({ gradeId, subjectId });
      }
    }
  }
  return out;
}

export function EntitlementPicker({
  grades,
  value,
  onChange,
}: {
  grades: AdminGrade[];
  value: PickerValue;
  onChange: (v: PickerValue) => void;
}) {
  const setMode = (gradeId: string, mode: GradeMode) =>
    onChange({ ...value, modes: { ...value.modes, [gradeId]: mode } });

  const toggleSubject = (gradeId: string, subjectId: string) => {
    const current = value.subjects[gradeId] ?? [];
    const next = current.includes(subjectId)
      ? current.filter((s) => s !== subjectId)
      : [...current, subjectId];
    onChange({
      ...value,
      // Ticking a subject implies the grade is in per-subject mode.
      modes: { ...value.modes, [gradeId]: next.length ? 'subjects' : 'none' },
      subjects: { ...value.subjects, [gradeId]: next },
    });
  };

  if (!grades.length) {
    return <p className="text-sm text-slate-500">No grades exist yet.</p>;
  }

  return (
    <div className="space-y-3">
      {grades.map((g) => {
        const mode = value.modes[g.id] ?? 'none';
        const ticked = value.subjects[g.id] ?? [];
        return (
          <div key={g.id} className="border border-slate-200 rounded-md p-3 bg-white">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-medium text-slate-900">{g.name}</span>
              <div className="flex items-center gap-1 text-sm">
                {(['none', 'whole', 'subjects'] as GradeMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(g.id, m)}
                    className={
                      'px-2 py-1 rounded border ' +
                      (mode === m
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50')
                    }
                  >
                    {m === 'none' ? 'No access' : m === 'whole' ? 'Whole grade' : 'Pick subjects'}
                  </button>
                ))}
              </div>
              <span className="text-xs text-slate-500">
                {mode === 'whole'
                  ? 'All subjects, including any added later'
                  : mode === 'subjects'
                    ? `${ticked.length} subject(s) selected`
                    : 'Hidden from this school'}
              </span>
            </div>

            {mode === 'subjects' && (
              <div className="mt-3 pl-3 border-l-2 border-slate-100 flex flex-wrap gap-x-4 gap-y-2">
                {g.subjects.length === 0 && (
                  <span className="text-sm text-slate-400">This grade has no subjects.</span>
                )}
                {g.subjects.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={ticked.includes(s.id)}
                      onChange={() => toggleSubject(g.id, s.id)}
                      className="rounded border-slate-300"
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
